import { useEffect, useState, useMemo } from "react";
import Select from "react-select";
import { useNavigate, useParams } from "react-router-dom";
import { redirectToLogin } from "../utils/auth";
import { History } from "../Components/Ticket/History";
import { Comments } from "../Components/Ticket/Comments";
import { TicketProgressBar } from "../Components/Ticket/TicketProgressBar";
import { Loading } from "../Components/Loading/Loading";
import { useUser } from "../context/UserContext";

// LOA purple theme (mirrors Form) for react-select controls + native inputs.
const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: "44px",
    borderRadius: "8px",
    fontSize: "12px",
    borderColor: state.isFocused ? "#3e0084" : "#d1d5db",
    boxShadow: state.isFocused ? "0 0 0 1px #3e0084" : "none",
    "&:hover": { borderColor: "#3e0084" },
  }),
  option: (base, state) => ({
    ...base,
    fontSize: "12px",
    backgroundColor: state.isSelected ? "#3e0084" : state.isFocused ? "#f4f0fa" : "white",
    color: state.isSelected ? "white" : "#141414",
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: "#f4f0fa",
    borderRadius: "15px",
    border: "1px solid #dcd0f0",
    paddingLeft: "4px",
  }),
  multiValueLabel: (base) => ({ ...base, color: "#3e0084", fontWeight: "bold", fontSize: "12px" }),
  multiValueRemove: (base) => ({
    ...base,
    color: "#3e0084",
    ":hover": { backgroundColor: "#dcd0f0", color: "#3e0084" },
  }),
  placeholder: (base) => ({ ...base, fontSize: "12px" }),
};

// 44px-high single-line control so native inputs/selects line up with react-select.
const controlClass =
  "w-full h-11 border border-gray-300 px-3 rounded-lg text-xs bg-white focus:outline-none focus:border-jiojbmpurple focus:ring-1 focus:ring-jiojbmpurple disabled:bg-gray-50 disabled:border-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed";

// Format a stored deadline for a native date/datetime-local input (local time).
const toDeadlineInput = (value, withTime) => {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "";
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString();
  return withTime ? local.slice(0, 16) : local.slice(0, 10);
};

// Shared ticket-detail implementation. `isRcs` picks the RCS config (severities
// / request types) — it is set by the JbmTicket / RcsTicket wrappers based on
// the ticket's own clientType, never from a URL.
export const TicketDetail = ({ isRcs }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [ticket, setTicket] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [requestType, setRequestType] = useState();
  const [priority, setPriority] = useState();
  const [issueAt, setIssueAt] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [requestOptions, setrequestOptions] = useState([]);
  const [ticketCategories, setTicketCategories] = useState([]); // JBM Type: Task/Issue
  const [priorities, setpriorities] = useState([]);
  const [deadline, setdeadline] = useState("");
  const [status, setstatus] = useState();
  const [org, setorg] = useState();
  const [assign, setassign] = useState("");
  const [users, setUsers] = useState([]);
  const [statusList, setStatusList] = useState([]);
  const [resolution, setResolution] = useState("");
  const [additionalInfo, setAdditionalInfo] = useState("");
  const [info, setInfo] = useState([]);
  const [isNeedMoreInfo, setIsNeedMoreInfo] = useState("0");
  const [isCrm, setIsCrm] = useState(false);
  const [collaborators, setCollaborators] = useState([]);
  // Emails the external RCS party added — read-only, shown so internal staff can see them.
  const [externalCollaborators, setExternalCollaborators] = useState("");
  const authToken = localStorage.getItem("authToken");
  const navigate = useNavigate();
  const { ticketID } = useParams();

  const { user } = useUser();
  const isPartner = user.isPartner ?? false;

  const tabs = useMemo(() => {
    const base = [
      { heading: "Request Details", id: "requestDetails" },
      { heading: "Discussion with Partner", id: "discussion" },
    ];
    if (!isPartner) {
      base.push({ heading: "Comments & History", id: "commentsHistory" });
    }
    base.push(
      { heading: "Resolution", id: "resolution" },
      { heading: "Media Upload", id: "mediaUpload" },
    );
    return base;
  }, [isPartner]);

  const userOptions = useMemo(
    () => users.map((u) => ({ value: u.email, label: u.name })),
    [users],
  );

  // JBM Type is derived (read-only here) from the ticket's request type category:
  // 0 = Task, 1 = Issue, null/unknown = "other". Drives the read-only Type field
  // and filters the request-type list to that Type.
  const currentReqOpt = requestOptions.find((o) => String(o.value) === String(requestType));
  const derivedTypeKey = currentReqOpt == null ? "" : currentReqOpt.category ?? "other";
  const detailRequestOptions = isRcs
    ? requestOptions
    : derivedTypeKey === ""
    ? requestOptions
    : derivedTypeKey === "other"
    ? requestOptions.filter((o) => !ticketCategories.some((c) => c.key === o.category))
    : requestOptions.filter((o) => o.category === derivedTypeKey);

  useEffect(() => {
    if (!authToken) {
      redirectToLogin();
      return;
    }
    if (!user.loaded) return;

    const BASE = process.env.REACT_APP_BACKEND_URL;
    const ticketId = ticketID;
    const tagQ = isRcs ? "?tag=rcs" : "";
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${authToken}`,
    };
    const get = (url) =>
      fetch(url, { method: "GET", headers }).then((r) => {
        if (r.status === 401) {
          redirectToLogin();
          return null;
        }
        return r.json();
      });

    const calls = [
      get(`${BASE}/API/V2/Tickets/${ticketId}`),                  // ticket
      get(`${BASE}/API/V2/config/requesttypes${tagQ}`),           // requestOptions
      get(`${BASE}/API/V2/config/priorities${tagQ}`),             // priorities
      get(`${BASE}/API/V2/config/status`),                        // statusList
      get(`${BASE}/API/V2/Tickets/${ticketId}/media`),            // files
      get(`${BASE}/API/V2/Tickets/${ticketId}/additional-info`),  // additionalInfo
      get(`${BASE}/API/V2/config/ticket-categories`),             // ticketCategories (Task/Issue)
      ...(!isPartner
        ? [
            get(`${BASE}/API/V2/users${isRcs ? "" : "?clientType=internal"}`), // users (RCS: all; JBM: internal only)
            get(`${BASE}/API/V2/users/${encodeURIComponent(user.username)}`), // current user (for isCrm)
          ]
        : []),
    ];

    Promise.all(calls)
      .then(([ticketData, requestTypesData, prioritiesData, statusData, filesData, additionalInfoData, ticketCategoriesData, usersData, currentUserData]) => {
        if (!ticketData) return;
        if (ticketData.error) {
          if (ticketData.error === "Invalid Organisation ID") {
            alert("Invalid Organisation ID");
            redirectToLogin();
          }
          return;
        }
                const sortedPriorities = [...prioritiesData].sort(
          (a, b) => Number(a.value) - Number(b.value)
        );

        setTicket(ticketData);
        setTitle(ticketData.title);
        setDescription(ticketData.description);
        setRequestType(ticketData.requestType);
        setPriority(ticketData.priority);
        setIssueAt(ticketData.issueAt || "");
        setExternalCollaborators(ticketData.externalCollaborators || "");
        setdeadline(ticketData.deadline);
        setorg(ticketData.org);
        setstatus(ticketData.status);
        setResolution(ticketData.resolution || "");
        setIsNeedMoreInfo(ticketData.isNeedMoreInfo);

        setrequestOptions(requestTypesData || []);
        setTicketCategories(Array.isArray(ticketCategoriesData) ? ticketCategoriesData : []);
        setpriorities(sortedPriorities || []);
        setStatusList(statusData || []);
        setUploadedFiles(filesData || []);
        setInfo(additionalInfoData || []);

        if (!isPartner) {
          setUsers(usersData || []);
          // RCS tickets: only RCS CSMs may post to the partner discussion; JBM
          // tickets keep the customer_support gate. Mirrors the server-side check.
          setIsCrm(
            (isRcs ? currentUserData?.role?.rcs_csm : currentUserData?.role?.customer_support) ?? false,
          );
        }

        if (ticketData.assign) {
          setassign(ticketData.assign);
          setCollaborators((ticketData.collaborators || "").split(","));
        }
      })
      .catch((err) => console.error("Ticket load error:", err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.loaded]);

  const fileSelectedHandler = (event) => {
    const incoming = Array.from(event.target.files);
    const combined = [...selectedFiles, ...incoming];
    if (combined.length > 10) {
      alert("You can upload a maximum of 10 files.");
    }
    setSelectedFiles(combined.slice(0, 10));
  };

  const removeSelectedFile = (index) => {
    setSelectedFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  const removeFileHandler = async (fileName) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/API/V2/Tickets/${ticket.ticketId}/media/${fileName}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to delete file");
      }

      setUploadedFiles((prevFiles) =>
        prevFiles.filter((file) => file !== fileName),
      );
    } catch (error) {
      console.error("Error deleting file:", error);
      alert("An error occurred while deleting the file.");
    }
  };

  useEffect(() => {
    if (isPartner) {
      // Mask the internal assignee, except when it's the creator's turn (Need
      // More Info / Resolved / Closed) — then show whom it's assigned to.
      const waiting = status == 5 || status == 3 || status == 4;
      setassign(waiting ? ticket.createdBy || "" : "JBM Customer Experience");
    }
  }, [isPartner, status, ticket]);

  function update() {
    if (!authToken) {
      alert("Unauthorized access. Please log in.");
      return;
    }

    if (!description || !priority || !deadline || !status) {
      alert("Please fill all required fields.");
      return;
    }
    if (!resolution && status == 3 && !isPartner) {
      alert("Please Provide Resolution");
      return;
    }
    const formData = new FormData();
    if (isPartner) {
      formData.append("description", description);
    }

    if (!isPartner) {
      formData.append("assign", assign);
      formData.append("priority", priority);
      formData.append("deadline", deadline);
      formData.append("resolution", resolution);
      formData.append("collaborators", collaborators.join(","));
    }

    selectedFiles.forEach((file, index) => {
      formData.append(`files[${index}]`, file);
    });
    formData.append("additionalInfo", additionalInfo);
    if (status == 5) {
      formData.append("isNeedMoreInfo", "1");
    }
    formData.append("status", status);

    fetch(
      `${process.env.REACT_APP_BACKEND_URL}/API/V2/Tickets/${ticketID}`,
      {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
        body: formData,
      },
    )
      .then((response) => response.json())
      .then((data) => {
        if (data.error) {
          alert("Failed to update the ticket: " + data.error);
        } else {
          alert("Ticket updated successfully!");
          navigate("/dashboard");
        }
      })
      .catch((error) => {
        console.error("Error updating ticket:", error);
        alert("An error occurred while updating the ticket.");
      });
  }

  function getStatusKey(value) {
    const match = statusList.find((s) => s.value == value);
    return match?.key ?? `Unknown (${value})`;
  }

  const handleDownload = async (fileName) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/API/V2/Tickets/${ticket.ticketId}/media/${fileName}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to download file");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Error downloading file:", error);
    }
  };

  if (!user.loaded || !ticket) {
    return <Loading />;
  }

  const activeTabId = tabs[activeTab]?.id;

  // Internal status transitions: Open→Active/NeedInfo, Active→Resolved/NeedInfo,
  // Resolved/Closed/Reopened→Open (reopen); Need More Info is locked (waits on
  // the requester). Partners keep their own rule (close / reopen only).
  const INTERNAL_FLOW = {
    1: ["1", "2", "5"],
    2: ["2", "3", "5"],
    3: ["3", "1"],
    4: ["4", "1"],
    6: ["6", "2"],
    5: ["2","3","5","4"],
  };
  const allowedInternal = INTERNAL_FLOW[String(status)] || [String(status)];

  // Partner (external) access rules — keyed on the ticket's PERSISTED status
  // (ticket.status), not the editable dropdown value, so selecting "Closed"
  // doesn't lock the controls before the partner can save that transition.
  //  - Resolved (3) or Closed (4): partner discussion is disabled.
  //  - Closed (4): the ticket is final for partners — no status change, no update.
  const partnerDiscussionLocked =
    isPartner && (ticket.status == 3 || ticket.status == 4);
  const partnerClosed = isPartner && ticket.status == 4;

  // Partner (external) status transitions, keyed on the persisted status:
  //  - Need More Info (5): they've been asked for input, so the only move is
  //    back to Active (2) — respond and hand it back to support.
  //  - Otherwise: Close (4) or Reopen (6).
  const allowedPartner = String(ticket.status) === "5" ? ["2"] : ["4", "6"];

  return (
    <div className="flex h-[calc(100vh-72px)] overflow-hidden bg-gray-100">
      {/* Left — Ticket content */}
      <div className={`flex-1 flex flex-col ${activeTabId === "commentsHistory" ? "overflow-hidden" : "overflow-y-auto"}`}>
        <div className={`flex flex-col p-6 ${activeTabId === "commentsHistory" ? "pb-0 flex-1 min-h-0" : "min-h-full"}`}>
          {/* Ticket ID + Title header */}
          <div className="flex items-center gap-4 text-sm flex-shrink-0">
            <div className="flex items-center p-3 py-1 w-fit bg-jiojbm50 rounded-lg text-white font-black tracking-tight leading-snug">
              <p>{ticket.ticketId}</p>
            </div>
            <div className="font-black tracking-tight leading-snug uppercase">
              {title}
            </div>
          </div>

          {/* Tab Bar */}
          <div className="flex items-center ml-1 gap-6 mt-4 flex-shrink-0">
            {tabs.map((tab, index) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(index)}
                className="text-xs font-bold leading-snug transition-colors relative"
              >
                {tab.heading}
                {activeTab === index && (
                  <div className="absolute -bottom-1 left-0 right-0 h-1 rounded-full bg-jiojbmpurple" />
                )}
              </button>
            ))}
            <div className="ml-auto flex items-center">
              <button
                className="text-xs rounded-3xl px-3 py-1 font-bold tracking-tight leading-tight text-white bg-jiojbmpurple hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:opacity-50"
                type="button"
                onClick={() => update()}
                disabled={partnerClosed}
              >
                Update
              </button>
            </div>
          </div>

          {/* Tab: Request Details */}
          {activeTabId === "requestDetails" && (
            <div className="flex flex-col gap-2 mt-6 ml-1 pb-6 text-xs">
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-1 h-full">
                  <p>
                    Description <span className="text-jiored1">*</span>
                  </p>
                  <textarea
                    className="w-full flex-1 min-h-[10rem] border border-gray-300 px-3 py-2 resize-none rounded-lg text-xs focus:outline-none focus:border-jiojbmpurple focus:ring-1 focus:ring-jiojbmpurple disabled:bg-gray-50 disabled:border-gray-200 disabled:cursor-not-allowed"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    disabled={!isPartner || ticket.status != 5}
                  />
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-4 self-start">
                  {!isRcs && (
                    <div className="flex flex-col gap-1">
                      <p>
                        Type <span className="text-jiored1">*</span>
                      </p>
                      <select
                        className={controlClass}
                        value={derivedTypeKey === "" ? "" : String(derivedTypeKey)}
                        disabled
                      >
                        <option value="">--</option>
                        {ticketCategories.map((c) => (
                          <option key={c.key} value={String(c.key)}>
                            {c.value}
                          </option>
                        ))}
                        {/* <option value="other">Other</option> */}
                      </select>
                    </div>
                  )}
                  <div className="flex flex-col gap-1">
                    <p>
                      Service Request Type{" "}
                      <span className="text-jiored1">*</span>
                    </p>
                    <select
                      className={controlClass}
                      value={requestType}
                      onChange={(e) => setRequestType(e.target.value)}
                      disabled
                    >
                      <option value="">--Select Request--</option>
                      {detailRequestOptions.map((requestOption, index) => (
                        <option key={index} value={requestOption.value}>
                          {requestOption.key}
                        </option>
                      ))}
                    </select>
                  </div>
                  {/* {isRcs && (
                    <div className="flex flex-col gap-1">
                      <p>Issue At</p>
                      <input
                        type="text"
                        className={controlClass}
                        value={issueAt}
                        disabled
                        readOnly
                      />
                    </div>
                  )} */}
                  <div className="flex flex-col gap-1">
                    <p>
                      Priority <span className="text-jiored1">*</span>
                    </p>
                    <select
                      className={controlClass}
                      value={priority}
                      onChange={(e) => setPriority(e.target.value)}
                      disabled={isPartner}
                    >
                      <option value="">--Select Priority--</option>
                      {priorities.map((priority, index) => (
                        <option key={index} value={priority.value}>
                          {priority.key}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex flex-col gap-1">
                    <p>
                      Deadline <span className="text-jiored1">*</span>
                    </p>
                    <input
                      type={isRcs ? "datetime-local" : "date"}
                      className={controlClass}
                      value={toDeadlineInput(deadline, isRcs)}
                      onChange={(e) => setdeadline(e.target.value)}
                      min={isRcs ? undefined : new Date().toISOString().split("T")[0]}
                      disabled={isPartner}
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <p>
                      Status <span className="text-jiored1">*</span>
                    </p>
                    <select
                      className={controlClass}
                      value={status}
                      onChange={(e) => {
                        setstatus(e.target.value);
                        if (e.target.value == 5) {
                          setIsNeedMoreInfo("1");
                        }
                      }}
                      disabled={partnerClosed}
                    >
                      {["1", "2", "3", "5", "6", "4"].map((s) => {
                        const disabled = isPartner
                          ? !allowedPartner.includes(s)
                          : !allowedInternal.includes(s);
                        return (
                          <option key={s} value={s} disabled={disabled}>
                            {getStatusKey(s)}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                  {!isPartner && (
                    <div className="flex flex-col gap-1">
                      <p>
                        Organisation <span className="text-jiored1">*</span>
                      </p>
                      <input
                        type="text"
                        disabled
                        className={controlClass}
                        value={org}
                      />
                    </div>
                  )}
                  <div className="flex flex-col gap-1">
                    <p>
                      Assigned <span className="text-jiored1">*</span>
                    </p>
                    {!isPartner && (status == 5 || status == 3 || status == 4) ? (
                      // Need More Info (5) / Resolved (3) / Closed (4): it's the
                      // creator's turn, so show the creator read-only. The underlying
                      // assignee is left unchanged and is still what's submitted on save.
                      <input
                        className={controlClass}
                        value={
                          users.find((u) => u.email === ticket.createdBy)?.name ||
                          ticket.createdBy ||
                          ""
                        }
                        disabled
                        readOnly
                      />
                    ) : (
                      <select
                        className={controlClass}
                        value={assign}
                        onChange={(e) => setassign(e.target.value)}
                        disabled={isPartner}
                      >
                        {users.map((u) => (
                          <option key={u.email} value={u.email}>
                            {u.name}
                          </option>
                        ))}
                        {isPartner && <option>{assign}</option>}
                      </select>
                    )}
                  </div>
                  {!isPartner && (
                    <div className="flex flex-col gap-1 col-span-2">
                      <p>Collaborators</p>
                      <Select
                        isMulti
                        options={userOptions}
                        value={collaborators
                          .filter(Boolean)
                          .map(
                            (email) =>
                              userOptions.find((o) => o.value === email) || {
                                value: email,
                                label: email,
                              },
                          )}
                        onChange={(selected) =>
                          setCollaborators(
                            selected
                              ? selected.map((option) => option.value)
                              : [],
                          )
                        }
                        placeholder="Select collaborators..."
                        className="react-select-container"
                        classNamePrefix="react-select"
                        styles={selectStyles}
                      />
                    </div>
                  )}
                  {!isPartner && isRcs && (
                    <div className="flex flex-col gap-1 col-span-2">
                      <p>External Collaborators</p>
                      <input
                        type="text"
                        className={controlClass}
                        value={externalCollaborators}
                        placeholder="Added by the requester"
                        disabled
                        readOnly
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Tab: Discussion */}
          {activeTabId === "discussion" && (
            <div className="flex flex-col gap-2 mt-6 ml-1 pb-6 text-xs flex-1">
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 flex-1 flex flex-col">
                <div className="flex flex-col gap-1 flex-1">
                  <p>Additional Information</p>
                  <textarea
                    placeholder="Add Additional Information"
                    className="w-full flex-1 min-h-[5rem] border border-gray-300 bg-white px-3 py-2 rounded-lg text-xs focus:outline-none focus:border-jiojbmpurple focus:ring-1 focus:ring-jiojbmpurple disabled:bg-gray-50 disabled:cursor-not-allowed"
                    value={additionalInfo}
                    onChange={(e) => setAdditionalInfo(e.target.value)}
                    disabled={!(isPartner || isCrm || isRcs) || partnerDiscussionLocked}
                  ></textarea>
                </div>
                {info.length > 0 && (
                  <div className="mt-4 space-y-3 max-h-64 overflow-y-auto">
                    {info.map((comment, index) => (
                      <div
                        key={index}
                        className="w-full border bg-gray-100 py-2 px-3 rounded-lg"
                      >
                        <div className="flex items-center space-x-2">
                          <p className="font-semibold text-gray-700">
                            {comment.commentedBy}
                          </p>
                          <span className="text-gray-500">
                            {comment.createdAt &&
                              new Date(comment.createdAt).toLocaleString()}
                          </span>
                        </div>
                        <p className="mt-1 text-gray-600 whitespace-pre-line break-words">
                          {comment.commentText}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Tab: Resolution */}
          {activeTabId === "resolution" && (
            <div className="flex flex-col gap-2 mt-6 ml-1 pb-6 text-xs flex-1">
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 flex-1 flex flex-col">
                <div className="flex flex-col gap-1 flex-1">
                  <p>
                    Resolution Description{" "}
                    <span className="text-jiored1">*</span>
                  </p>
                  <textarea
                    className="w-full flex-1 min-h-[10rem] border border-gray-300 px-3 py-2 resize-none rounded-lg text-xs focus:outline-none focus:border-jiojbmpurple focus:ring-1 focus:ring-jiojbmpurple disabled:bg-gray-50 disabled:border-gray-200 disabled:cursor-not-allowed"
                    value={resolution}
                    onChange={(e) => setResolution(e.target.value)}
                    disabled={isPartner}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Tab: Media Upload */}
          {activeTabId === "mediaUpload" && (
            <div className="flex flex-col gap-2 mt-6 ml-1 pb-6 text-xs flex-1">
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 flex-1">
                <input
                  className="block w-full text-xs text-gray-600 file:mr-3 file:cursor-pointer file:rounded-full file:border-0 file:bg-jiojbmpurple file:px-4 file:py-2 file:font-bold file:text-white hover:file:opacity-90"
                  type="file"
                  onChange={fileSelectedHandler}
                  multiple
                  accept=".xls,.xlsx,.pdf,.jpg,.jpeg,.png,.gif,.doc,.docx,.json,.csv,.pcap,.pcapng,.mp4,.mov,.avi,.mkv,.webm,.m4v"
                />
                {selectedFiles.length > 0 && (
                  <div className="mt-4">
                    <p className="font-semibold">Selected Files:</p>
                    <ul>
                      {selectedFiles.map((file, index) => (
                        <li
                          key={index}
                          className="flex justify-between items-center border border-gray-300 p-2 px-3 my-1 rounded-lg"
                        >
                          <span
                            className="text-blue-500 hover:underline cursor-pointer"
                            onClick={() => handleDownload(file)}
                          >
                            {file.name}
                          </span>
                          <button
                            type="button"
                            className="text-jiored1 hover:text-jiored1"
                            onClick={() => removeSelectedFile(index)}
                          >
                            ❌
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {uploadedFiles.length > 0 && (
                  <div className="mt-4">
                    <p className="font-semibold">Uploaded Files:</p>
                    <ul>
                      {uploadedFiles.map((file, index) => (
                        <li
                          key={index}
                          className="flex justify-between items-center bg-gray-100 p-2 my-1 rounded-md"
                        >
                          <span
                            className="text-blue-500 hover:underline cursor-pointer"
                            onClick={() => handleDownload(file)}
                          >
                            {file}
                          </span>
                          <button
                            type="button"
                            className="text-jiored1 hover:text-jiored1"
                            onClick={() => removeFileHandler(file)}
                          >
                            ❌
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Tab: Comments & History (non-partner only) */}
          {activeTabId === "commentsHistory" && !isPartner && (
            <div className="flex flex-col mt-6 ml-1 pb-6 text-xs flex-1 min-h-0">
              <div className="grid grid-cols-2 gap-4 flex-1 min-h-0">
                <div className="col-span-1 min-h-0 flex flex-col">
                  <Comments
                    ticketId={ticket.ticketId}
                    org={ticket.org}
                    authToken={authToken}
                    users={users}
                  />
                </div>
                <div className="col-span-1 min-h-0 flex flex-col">
                  <History ticketId={ticket.ticketId} authToken={authToken} />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Right — Vertical Progress Bar */}
      <div className="flex justify-center">
        <TicketProgressBar
          ticketId={ticket.ticketId}
          authToken={authToken}
          ticket={ticket}
          isPartner={isPartner}
        />
      </div>
    </div>
  );
};
